import React, { useState, useEffect, useMemo } from "react";
import ReactECharts from "echarts-for-react";
import "echarts-wordcloud";
import { getL1SheetProperty } from "../../../features/reduxworkbook/WorkbookSlice";
import { useSelector } from "react-redux";
import { getColourSchemeByID } from "../../../Colors/ColorPalettes";
import { getSheetControls } from "../../../features/reduxworkbook/WorkbookControllingSlice";
import { formatNumber } from "../Utils/FormatNumbers";
import { useMyContext } from "../../../MyContext";

const WordCloudChartComp = ({
  Data,
  Sheetid,
  valueKeys,
  toolTipKeys,
  groupByKeys: rawRowKeys,
}) => {
  const { isDownloadImage } = useMyContext();
  const [words, setWords] = useState([]);
  const [chartInstance, setChartInstance] = useState(null); // Store chart instance

  const addTransparency = (hex, alpha) => {
    const alphaHex = Math.round(alpha * 255)
      .toString(16)
      .padStart(2, "0");
    return `${hex}${alphaHex}`;
  };

  const customizationObject = useSelector((state) =>
    getL1SheetProperty(state, Sheetid, "customization")
  );

  const { colorSchemeid, transparency = 100, isTileVisible, isTitleBold, Title, TitleFontColor, TitleSize, valueAlign } = customizationObject;
  // console.log(isTileVisible, Title, TitleFontColor, TitleSize, valueAlign, isTitleBold);

  const colorScheme = getColourSchemeByID(colorSchemeid);
  const COLORS = (colorScheme?.colors || []).map((color) =>
    addTransparency(color, transparency / 100)
  );


  useEffect(() => {
    const labelKey = rawRowKeys?.[0]?.responseKey;
    const valueKey = valueKeys?.[0]?.responseKey;

    if (!Array.isArray(Data) || !labelKey) {
      setWords([]);
      return;
    }

    // ✅ Recompute COLORS inside the effect
    const COLORS = (colorScheme?.colors || []).map((color) =>
      addTransparency(color, transparency / 100)
    );

    const wordData = Data.map((item, index) => ({
      name: item?.[labelKey] ?? "",
      value: Number(item?.[valueKey]) || 1,
      textStyle: {
        color: COLORS[index % COLORS.length],
      },
    }));

    setWords(wordData);
  }, [Data, rawRowKeys, valueKeys, colorScheme, transparency]);

  const option = {
    tooltip: {
      backgroundColor: "transparent",
      borderWidth: 0,
      padding: 0,
      formatter: function (params) {
        const data = params?.data?.rawData || {};
        const valueKeyCaption = valueKeys[0]?.caption;

        const numberFormat = valueKeys[0]?.numberFormat;

        const formattedValue = formatNumber(params.value, numberFormat);

        const heading = `<div style="font-weight: 600; font-size: 16px;">${params.name}</div>`;
        const content = `<div style="font-size: 15px; margin-top: 2px;">${valueKeyCaption}: ${formattedValue}</div>`;

        const additional = toolTipKeys
          ?.map(({ responseKey, caption, numberFormat }) => {
            const label = caption || responseKey;

            //  If Data is an array, use Data[0], otherwise just Data
            const dataItem = Array.isArray(Data) ? Data[0] : Data;

            const rawValue = dataItem?.[responseKey];
            const isNumeric = typeof rawValue === "number";
            const value = isNumeric ? Number(rawValue) : rawValue ?? "N/A";

            const formattedValue = isNumeric
              ? formatNumber(value, numberFormat)
              : value;

            return `<div style="font-size: 15px; color: #4B5563;">${label}: ${formattedValue}</div>`;
          })
          .join("");

        return `
      <div 
        class="border border-gray-200 rounded bg-white p-[10px] "
        style="
          background: rgba(255, 255, 255, 0.9);
          padding: 10px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          border-radius: 6px;
        "
      >
        <div style="color: #374151; display: flex; flex-direction: column; gap: 4px;">
          ${heading}
          ${content}
          ${additional}
        </div>
      </div>
    `;
      },
      extraCssText: "pointer-events: auto;",
    },

    series: [
      {
        top: "1%",
        bottom: "2%",
        type: "wordCloud",
        gridSize: 5,
        sizeRange: [20, 80],
        rotationRange: [0, 0],
        shape: "circle",
        textStyle: {
          normal: {
            color: () => COLORS[Math.floor(Math.random() * COLORS.length)],
          },
          emphasis: {
            shadowBlur: 10,
            shadowColor: "#333",
          },
        },
        data: words,
      },
    ],
  };

  // Download chart as image
  const downloadChartAsImage = () => {
    if (!chartInstance) return;

    const dataURL = chartInstance.getDataURL({
      pixelRatio: 2,
      backgroundColor: "#ffffff", // Set background color
    });

    const a = document.createElement("a");
    a.href = dataURL;
    a.download = `wordcloud-${Date.now()}.png`;
    a.click();
  };
  useEffect(() => {
    if (isDownloadImage) {
      downloadChartAsImage();
    }
  }, [isDownloadImage]);

  return (
    <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Title Section */}
      {isTileVisible && (
        <div
          className={`w-full ${isTitleBold ? "font-bold" : ""} ${valueAlign === "left"
            ? "text-left"
            : valueAlign === "right"
              ? "text-right"
              : "text-center"
            }`}
          style={{
            fontSize: TitleSize,
            color: TitleFontColor ?? "#bababa",
            paddingBottom: "8px", // Adds spacing between title and chart
          }}
        >
          {Title || " Title..."}
        </div>
      )}

      {/* Chart Section */}
      <div style={{ flex: 1 }}>
        <ReactECharts
          option={option}
          notMerge={true}
          style={{ height: isTileVisible ? "95%" : "100%", width: "100%" }}
          onChartReady={(chart) => setChartInstance(chart)}
        />
      </div>
    </div>

  );
};

export default WordCloudChartComp;
